import React, { useState } from 'react';
import { Autocomplete, Chip, TextField } from '@mui/material';

type TagEditorProps = {
  tags: string[];
  canEdit: boolean;
  onSave: (tags: string[]) => Promise<void>;
};

/**
 * Chip-based tag editor. Read-only users see plain chips; editors get a freeSolo
 * autocomplete where typing + Enter adds a tag and chip deletion removes one.
 */
const TagEditor: React.FC<TagEditorProps> = ({ tags, canEdit, onSave }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!canEdit) {
    return tags.length === 0 ? null : (
      <>
        {tags.map((tag) => (
          <Chip key={tag} label={tag} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
        ))}
      </>
    );
  }

  return (
    <Autocomplete
      multiple
      freeSolo
      options={[]}
      value={tags}
      disabled={saving}
      onChange={async (_event, value) => {
        const next = value
          .map((tag) => (typeof tag === 'string' ? tag.trim() : ''))
          .filter((tag) => tag.length > 0);
        setSaving(true);
        setError(null);
        try {
          await onSave(Array.from(new Set(next)));
        } catch (e: any) {
          setError(e?.response?.data?.message || e?.message || 'Failed to save tags.');
        } finally {
          setSaving(false);
        }
      }}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip label={option} size="small" {...getTagProps({ index })} />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          placeholder={tags.length === 0 ? 'Add tags…' : ''}
          error={!!error}
          helperText={error || 'Press Enter to add a tag'}
        />
      )}
    />
  );
};

export default TagEditor;
